import { ChangeEvent, useCallback, useEffect, useMemo, useRef } from 'react'
import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import { setDeploymentSetup } from 'redux/features/emailServer/emailServerSlice'
import { isMyFeatureOn } from 'lib/splitio'
import { FEATURES } from 'lib/splitioFeatures'
import { trackEventInAllServices, TRACKING_EVENTS } from 'lib/monitoring/monitoringService'
import { DeploymentSetupOptions } from '../../types/egdWizardTypes'

export interface State {
  deploymentSetup: DeploymentSetupOptions
  inlineDisabled: boolean
  mxRecordDisabled: boolean
  isMultipleDomains: boolean
}

export interface EventHandlers {
  onSelectChange: (e: ChangeEvent<HTMLInputElement>) => void
}

export type DeploymentSetupLogic = [State, EventHandlers]

export const useDeploymentSetupLogic = (): DeploymentSetupLogic => {
  const dispatch = useAppDispatch()
  const isInlineDeploymentRollout = isMyFeatureOn(FEATURES.EGD_React_Inline_Deployment_Rollout)
  const hasAutoSelectedInlineRef = useRef(false)

  const { deploymentSetup, emailServerSettings } = useAppSelector(_store => ({
    deploymentSetup: _store.emailServer.deploymentSetup,
    emailServerSettings: _store.emailServer.emailServerSettings
  }))

  // Check if multiple domains are selected from the persisted emailServers (not msDomainNames which is UI-only)
  const emailServersCount = emailServerSettings?.emailServers?.length || 0
  const isMultipleDomains = emailServersCount > 1

  useEffect(() => {
    trackEventInAllServices(TRACKING_EVENTS.SETUP_FLOW.STEP_4_START)
    // eslint-disable-next-line
  }, [])

  // Auto-select inline deployment when multiple domains are selected
  useEffect(() => {
    if (isMultipleDomains && !hasAutoSelectedInlineRef.current) {
      hasAutoSelectedInlineRef.current = true
      dispatch(setDeploymentSetup(DeploymentSetupOptions.inline))
    }
  }, [isMultipleDomains, dispatch])

  const onSelectChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      trackEventInAllServices(TRACKING_EVENTS.SETUP_FLOW.STEP_4_CHANGE, { selectedDeploymentMethod: e.target.value })
      dispatch(setDeploymentSetup(e.target.value as DeploymentSetupOptions))
    },
    [dispatch]
  )

  // MX record is disabled when multiple domains are selected (Phase 1: inline-only for multi-domain)
  const mxRecordDisabled = isMultipleDomains

  // Inline is disabled only when NOT multi-domain AND missing IPIR credentials or feature flag
  // When multiple domains are selected, Inline is always enabled (it's the only option)
  const inlineDisabled = isMultipleDomains
    ? false
    : !emailServerSettings?.ipirAccountId || !emailServerSettings?.ipirTokenId || !isInlineDeploymentRollout

  return useMemo(
    () => [
      {
        deploymentSetup,
        inlineDisabled,
        mxRecordDisabled,
        isMultipleDomains
      },
      {
        onSelectChange
      }
    ],
    [deploymentSetup, inlineDisabled, mxRecordDisabled, isMultipleDomains, onSelectChange]
  )
}
